/**
 * The catalogue of widgets a course admin can place on a map.
 *
 * Every widget is drawn at its base point (bottom centre at the origin) so
 * the maps can drop it on a ground line with one translate. SVG widgets take
 * the land's ground colour for a light ambient blend and its accent for
 * details; plush widgets are React components positioned in HTML; the text
 * widget shows a short label. Probability and AI toys are HTML widgets
 * that stay clickable on the map (see {@link WidgetDefinition.isInteractive}),
 * and link widgets open a callout and then their link (see
 * {@link WidgetDefinition.link}).
 */
import type {ReactNode} from 'react';
import {
  ToyAutocomplete,
  ToyClassifier,
  ToyGradientDescent,
  ToyNeuron,
  ToyTemperature,
} from '../../cuteWidgets/aiMapToys';
import {
  ToyBeanMachine,
  ToyCoin,
  ToyDarts,
  ToyDie,
  ToyMarbleJar,
} from '../../cuteWidgets/mapToys';
import {
  GoodieRecordPlayer,
  GoodieTextbook,
  GoodieTshirt,
  ToyCityLights,
} from '../../cuteWidgets/goodieMapToys';
import {
  ToyFarmCow,
  ToyJacaranda,
  ToyLakeLagunita,
  ToyMarguerite,
  ToyMemorialChurch,
} from '../../cuteWidgets/campusMapToys';
import {MysteryEgg} from '../../cuteWidgets/MysteryEgg';
import {ToyRainCloud} from '../../poissonRain/RainCloudToy';
import {ToyCookieOven} from '../../cuteWidgets/cookieOven';
import {ToyOlliePorch} from '../../cuteWidgets/olliePorch';
import {
  ToyOllieFloat,
  ToyOllieJuggling,
  ToyOttersHoldingPaws,
} from '../../cuteWidgets/ollieMapToys';
import {
  ToyAutofocusCamera,
  ToyFoggyMountain,
  ToyPyramid,
} from '../../cuteWidgets/week3MapToys';
import {
  ToyDoodleMachine,
  ToySigmoidSlide,
  ToySortingLine,
} from '../../cuteWidgets/week4MapToys';
import {
  ToyBike,
  ToyDancingTree,
  ToyDish,
  ToyFountain,
  ToyHooverTower,
} from '../../cuteWidgets/stanfordMapToys';
import {OlliePlush} from '../../mascot/OlliePlush';
import {PaiPlush, PLUSH_STATES, type PlushState} from '../../mascot/PaiPlush';
import {PlushPi} from '../../mascot/PlushPi';
import {darken, lighten, mix} from '../mapColor';
import {
  BroadTree,
  Conifer,
  Daisies,
  LilyPond,
  Rocks,
  Shrub,
  Signpost,
} from '../worldScenery';

/** What a widget needs to draw itself. */
export interface WidgetRenderContext {
  /** The land's ground colour. */
  ground: string;
  /** The land's accent colour. */
  accent: string;
  /** Uniform scale applied by the caller; widgets may size text by it. */
  scale: number;
  /** Widget-specific option, e.g. a mascot's expression. */
  variant?: string;
  /** Free text for widgets that show one. */
  text?: string;
  /**
   * The widget is being drawn as a palette thumbnail, inside a button, so
   * interactive widgets must render a static picture. Omitted means a live
   * placement on a map.
   */
  isPreview?: boolean;
}

/** Where a widget sits in the palette. */
export type WidgetCategory =
  | 'nature'
  | 'camp'
  | 'landmarks'
  | 'sky'
  | 'friends'
  | 'toys'
  | 'ai'
  | 'surprises'
  | 'goodies'
  | 'stanford'
  | 'ollie'
  | 'week3'
  | 'week4'
  | 'notes';

/** How a widget is drawn. */
export type WidgetKind = 'svg' | 'html';

/** Where a link widget goes, and what its callout says. */
export interface WidgetLink {
  /** The callout's heading and the widget's accessible name. */
  title: string;
  /** One line about where the link goes. */
  description: string;
  /**
   * The destination: an absolute URL opens in a new tab, an in-app path in
   * the same tab.
   *
   * @param args.courseId - The course the map belongs to.
   * @return The URL or path.
   */
  href: (args: {courseId: string}) => string;
}

/** One entry in the catalogue. */
export interface WidgetDefinition {
  /** Stable id stored in placements. */
  id: string;
  label: string;
  category: WidgetCategory;
  kind: WidgetKind;
  /** Approximate drawn height at scale 1, for palette thumbnails and hit boxes. */
  height: number;
  /** Approximate drawn width at scale 1. */
  width: number;
  /** The options for {@link WidgetRenderContext.variant}, when the widget has any. */
  variants?: readonly {id: string; label: string}[];
  /** Whether the widget shows {@link WidgetRenderContext.text}. */
  hasText?: boolean;
  /**
   * The widget takes clicks on the map (an `html` widget only). Omitted
   * means decorative: the map lets clicks pass through it.
   */
  isInteractive?: boolean;
  /**
   * Makes the widget a two-press link (an `html` widget only): the map wraps
   * its picture in a button whose first press opens a callout and whose
   * second press follows the link. Omitted means the widget is not a link.
   */
  link?: WidgetLink;
  /**
   * Draws the widget. An `svg` widget returns SVG children positioned with
   * the base point at the origin; an `html` widget returns an element whose
   * bottom centre the caller positions.
   */
  render: (context: WidgetRenderContext) => ReactNode;
}

/** The course textbook the textbook widget opens. */
const COURSE_TEXTBOOK_URL = 'https://probabilitycoders.stanford.edu/pai';

const AMBIENT = 0.2;
const WOOD = '#8a5a3c';
const WOOD_DARK = '#6b4530';
const CANVAS = '#f1e7d4';
const FLAME = '#f28c28';
const FLAME_HOT = '#ffd35c';
const STONE = '#c9c4bb';
const STONE_DARK = '#a8a29a';
const LEAF = '#5cb85c';

const blend = (color: string, ground: string) => mix(color, ground, AMBIENT);

const shadow = (rx: number) => (
  <ellipse cx={0} cy={2} rx={rx} ry={rx * 0.22} fill="rgba(0,0,0,0.08)" />
);

/** A ridge tent with an open flap. */
function Tent({ground, accent}: WidgetRenderContext) {
  const canvas = blend(CANVAS, ground);
  const canvasDark = mix(canvas, accent, 0.18);
  return (
    <g>
      {shadow(34)}
      <path d="M-34 0 L0 -46 L34 0 Z" fill={canvas} />
      <path d="M0 -46 L34 0 L12 0 L0 -30 Z" fill={canvasDark} />
      <path d="M-8 0 L0 -26 L8 0 Z" fill={darken(canvasDark, 0.35)} />
      <path d="M-2 -46 L2 -46 L0 -56 Z" fill={blend(WOOD_DARK, ground)} />
      <path
        d="M-34 0 L34 0"
        stroke={blend(WOOD_DARK, ground)}
        strokeWidth={2}
      />
    </g>
  );
}

/** A campfire: logs, flame, sparks. */
function Campfire({ground}: WidgetRenderContext) {
  const wood = blend(WOOD, ground);
  return (
    <g>
      {shadow(18)}
      <path
        d="M-16 -2 L14 -10"
        stroke={wood}
        strokeWidth={5}
        strokeLinecap="round"
      />
      <path
        d="M-14 -10 L16 -2"
        stroke={darken(wood, 0.15)}
        strokeWidth={5}
        strokeLinecap="round"
      />
      <path d="M0 -8 C-12 -18 -8 -30 0 -40 C8 -30 12 -18 0 -8 Z" fill={FLAME} />
      <path
        d="M0 -10 C-6 -16 -4 -24 0 -30 C4 -24 6 -16 0 -10 Z"
        fill={FLAME_HOT}
      />
      <circle cx={-9} cy={-38} r={1.6} fill={FLAME_HOT} />
      <circle cx={8} cy={-44} r={1.3} fill={FLAME} />
    </g>
  );
}

/** A small cabin with a chimney. */
function Cabin({ground, accent}: WidgetRenderContext) {
  const wall = blend('#b98860', ground);
  const roof = mix(blend('#7d4e36', ground), accent, 0.2);
  return (
    <g>
      {shadow(40)}
      <rect x={-30} y={-36} width={60} height={36} fill={wall} />
      <path d="M-30 -36 H30" stroke={darken(wall, 0.2)} strokeWidth={1.5} />
      {[-26, -16, -6].map(y => (
        <path
          key={y}
          d={`M-30 ${y} H30`}
          stroke={darken(wall, 0.12)}
          strokeWidth={1}
        />
      ))}
      <path d="M-36 -36 L0 -62 L36 -36 Z" fill={roof} />
      <rect x={14} y={-58} width={7} height={14} fill={darken(roof, 0.2)} />
      <rect x={-6} y={-22} width={12} height={22} fill={darken(wall, 0.4)} />
      <rect x={-24} y={-28} width={11} height={10} fill="#dcecf2" />
      <path
        d="M-18.5 -28 V-18 M-24 -23 H-13"
        stroke={darken(wall, 0.4)}
        strokeWidth={1}
      />
      <rect x={13} y={-28} width={11} height={10} fill="#dcecf2" />
    </g>
  );
}

/** A park bench. */
function Bench({ground}: WidgetRenderContext) {
  const wood = blend(WOOD, ground);
  const iron = blend('#4a4440', ground);
  return (
    <g>
      {shadow(30)}
      <rect x={-26} y={-18} width={52} height={5} rx={1.5} fill={wood} />
      <rect x={-26} y={-30} width={52} height={5} rx={1.5} fill={wood} />
      <rect
        x={-26}
        y={-24}
        width={52}
        height={3}
        rx={1}
        fill={darken(wood, 0.1)}
      />
      <path
        d="M-22 0 V-32 M22 0 V-32"
        stroke={iron}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <path d="M-22 -18 H-16 M22 -18 H16" stroke={iron} strokeWidth={2.5} />
    </g>
  );
}

/** A lamp post with a warm lantern. */
function LampPost({ground}: WidgetRenderContext) {
  const iron = blend('#3d3835', ground);
  return (
    <g>
      {shadow(12)}
      <path d="M-8 0 H8" stroke={iron} strokeWidth={3} strokeLinecap="round" />
      <path d="M0 0 V-52" stroke={iron} strokeWidth={2.5} />
      <path d="M-8 -52 H8 L5 -64 H-5 Z" fill={iron} />
      <rect
        x={-5}
        y={-62}
        width={10}
        height={9}
        fill={FLAME_HOT}
        opacity={0.9}
      />
      <circle cx={0} cy={-58} r={12} fill={FLAME_HOT} opacity={0.14} />
      <path d="M0 -64 L0 -68" stroke={iron} strokeWidth={2} />
    </g>
  );
}

/** A trio of spotted mushrooms. */
function Mushrooms({ground, accent}: WidgetRenderContext) {
  const cap = mix(blend('#d9534f', ground), accent, 0.2);
  const stem = blend('#f3ead8', ground);
  const one = (x: number, s: number) => (
    <g key={x} transform={`translate(${x} 0) scale(${s})`}>
      <rect x={-4} y={-14} width={8} height={14} rx={3} fill={stem} />
      <path d="M-13 -12 C-13 -24 13 -24 13 -12 Z" fill={cap} />
      <circle cx={-5} cy={-17} r={1.8} fill="#fff" />
      <circle cx={4} cy={-19} r={1.4} fill="#fff" />
    </g>
  );
  return (
    <g>
      {shadow(20)}
      {one(-11, 0.85)}
      {one(4, 1.1)}
      {one(15, 0.7)}
    </g>
  );
}

/** A large boulder with a lichen highlight. */
function Boulder({ground, accent}: WidgetRenderContext) {
  const rock = blend(STONE, ground);
  const dark = blend(STONE_DARK, ground);
  return (
    <g>
      {shadow(30)}
      <path
        d="M-28 0 C-30 -18 -18 -30 -4 -30 C10 -32 26 -22 28 -8 C29 -3 26 0 22 0 Z"
        fill={rock}
      />
      <path
        d="M-28 0 C-24 -10 -10 -16 6 -16 C16 -16 24 -10 28 -4 L28 0 Z"
        fill={dark}
        opacity={0.35}
      />
      <path
        d="M-14 -22 C-8 -27 2 -28 8 -26"
        stroke="rgba(255,255,255,0.55)"
        strokeWidth={2}
        strokeLinecap="round"
        fill="none"
      />
      <circle
        cx={-6}
        cy={-10}
        r={4}
        fill={mix(LEAF, ground, 0.5)}
        opacity={0.6}
      />
      <circle
        cx={12}
        cy={-14}
        r={2.5}
        fill={mix(accent, ground, 0.6)}
        opacity={0.5}
      />
    </g>
  );
}

/** Two birds in flight. */
function Birds({ground}: WidgetRenderContext) {
  const ink = blend('#3d3835', ground);
  const bird = (x: number, y: number, s: number) => (
    <path
      key={`${x}-${y}`}
      d={`M${x - 8 * s} ${y} Q${x - 4 * s} ${y - 6 * s} ${x} ${y} Q${x + 4 * s} ${y - 6 * s} ${x + 8 * s} ${y}`}
      stroke={ink}
      strokeWidth={1.8}
      strokeLinecap="round"
      fill="none"
    />
  );
  return (
    <g>
      {bird(0, -30, 1)}
      {bird(18, -40, 0.8)}
      {bird(-14, -44, 0.6)}
    </g>
  );
}

/** A soft cloud. */
function Cloud({ground}: WidgetRenderContext) {
  const body = lighten(ground, 0.75);
  return (
    <g opacity={0.95}>
      <circle cx={-18} cy={-12} r={12} fill={body} />
      <circle cx={0} cy={-19} r={17} fill={body} />
      <circle cx={19} cy={-12} r={12} fill={body} />
      <rect x={-26} y={-14} width={52} height={14} rx={7} fill={body} />
    </g>
  );
}

/** A sun with soft rays. */
function Sun({ground}: WidgetRenderContext) {
  const glow = blend('#ffd35c', ground);
  return (
    <g>
      <circle cx={0} cy={-30} r={30} fill={glow} opacity={0.18} />
      <circle cx={0} cy={-30} r={18} fill={glow} />
      <circle cx={-6} cy={-36} r={5} fill={lighten(glow, 0.4)} />
    </g>
  );
}

/** A pennant flag on a pole. */
function Flag({ground, accent}: WidgetRenderContext) {
  const pole = blend(WOOD_DARK, ground);
  return (
    <g>
      {shadow(8)}
      <path
        d="M0 0 V-58"
        stroke={pole}
        strokeWidth={2.5}
        strokeLinecap="round"
      />
      <path d="M0 -58 L30 -50 L0 -42 Z" fill={accent} />
      <path d="M0 -58 L30 -50 L0 -42 Z" fill="rgba(255,255,255,0.18)" />
      <circle cx={0} cy={-59} r={2.5} fill={pole} />
    </g>
  );
}

/** A short run of picket fence. */
function Fence({ground}: WidgetRenderContext) {
  const wood = blend('#e7dcc8', ground);
  const dark = darken(wood, 0.25);
  return (
    <g>
      {shadow(36)}
      {[-30, -15, 0, 15, 30].map(x => (
        <path
          key={x}
          d={`M${x - 3} 0 V-24 L${x} -30 L${x + 3} -24 V0 Z`}
          fill={wood}
          stroke={dark}
          strokeWidth={0.8}
        />
      ))}
      <rect
        x={-36}
        y={-20}
        width={72}
        height={4}
        fill={wood}
        stroke={dark}
        strokeWidth={0.8}
      />
      <rect
        x={-36}
        y={-9}
        width={72}
        height={4}
        fill={wood}
        stroke={dark}
        strokeWidth={0.8}
      />
    </g>
  );
}

/** A hot-air balloon drifting by. */
function Balloon({ground, accent}: WidgetRenderContext) {
  const cloth = mix(accent, ground, 0.1);
  const stripe = lighten(cloth, 0.35);
  const basket = blend(WOOD, ground);
  return (
    <g>
      <path
        d="M-22 -60 C-22 -96 22 -96 22 -60 C22 -46 8 -36 0 -30 C-8 -36 -22 -46 -22 -60 Z"
        fill={cloth}
      />
      <path
        d="M-8 -62 C-8 -94 8 -94 8 -62 C8 -48 3 -38 0 -30 C-3 -38 -8 -48 -8 -62 Z"
        fill={stripe}
      />
      <path
        d="M-6 -30 L-5 -14 M6 -30 L5 -14"
        stroke={basket}
        strokeWidth={1.2}
      />
      <rect x={-8} y={-14} width={16} height={12} rx={2} fill={basket} />
      <path d="M-8 -8 H8" stroke={darken(basket, 0.2)} strokeWidth={1} />
    </g>
  );
}

/** A little wooden footbridge. */
function Bridge({ground}: WidgetRenderContext) {
  const wood = blend(WOOD, ground);
  const dark = blend(WOOD_DARK, ground);
  return (
    <g>
      {shadow(44)}
      <path
        d="M-44 0 C-30 -18 30 -18 44 0"
        stroke={wood}
        strokeWidth={7}
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M-40 -6 C-28 -22 28 -22 40 -6"
        stroke={dark}
        strokeWidth={1.8}
        fill="none"
      />
      {[-30, -15, 0, 15, 30].map(x => {
        const y = -8 - (1 - Math.abs(x) / 44) * 10;
        return (
          <path
            key={x}
            d={`M${x} ${y} V${y - 12}`}
            stroke={dark}
            strokeWidth={2}
          />
        );
      })}
    </g>
  );
}

const PLUSH_VARIANTS = PLUSH_STATES.map(state => ({
  id: state,
  label: state.charAt(0).toUpperCase() + state.slice(1),
}));

/**
 * Narrows a stored variant to a plush state.
 *
 * @param variant - The stored variant, if any.
 * @return The state, `idle` when the variant is missing or unknown.
 */
function plushState(variant: string | undefined): PlushState {
  return PLUSH_STATES.find(state => state === variant) ?? 'idle';
}

const NoteLabel = ({text, accent, scale}: WidgetRenderContext) => (
  <div
    style={{
      fontFamily: "'Source Sans 3', system-ui, sans-serif",
      fontSize: `${0.82 * scale}rem`,
      fontWeight: 600,
      color: darken(accent, 0.35),
      background: 'rgba(255,255,255,0.78)',
      border: `1px solid ${mix(accent, '#ffffff', 0.55)}`,
      borderRadius: 10,
      padding: `${5 * scale}px ${10 * scale}px`,
      whiteSpace: 'nowrap',
      boxShadow: '0 2px 6px rgba(31, 26, 23, 0.08)',
    }}
  >
    {text?.trim() || 'Note'}
  </div>
);

/** Every widget, in palette order. */
export const WIDGETS: readonly WidgetDefinition[] = [
  // Nature
  {
    id: 'broad-tree',
    label: 'Tree',
    category: 'nature',
    kind: 'svg',
    width: 60,
    height: 74,
    render: c => <BroadTree x={0} y={0} ground={c.ground} />,
  },
  {
    id: 'conifer',
    label: 'Pine',
    category: 'nature',
    kind: 'svg',
    width: 44,
    height: 62,
    render: c => <Conifer x={0} y={0} ground={c.ground} />,
  },
  {
    id: 'shrub',
    label: 'Shrub',
    category: 'nature',
    kind: 'svg',
    width: 50,
    height: 26,
    render: c => <Shrub x={0} y={0} ground={c.ground} />,
  },
  {
    id: 'pond',
    label: 'Pond',
    category: 'nature',
    kind: 'svg',
    width: 92,
    height: 30,
    render: c => <LilyPond x={0} y={0} ground={c.ground} />,
  },
  {
    id: 'daisies',
    label: 'Daisies',
    category: 'nature',
    kind: 'svg',
    width: 36,
    height: 24,
    render: c => <Daisies x={0} y={0} ground={c.ground} />,
  },
  {
    id: 'rocks',
    label: 'Rocks',
    category: 'nature',
    kind: 'svg',
    width: 44,
    height: 18,
    render: c => <Rocks x={0} y={0} ground={c.ground} />,
  },
  {
    id: 'boulder',
    label: 'Boulder',
    category: 'nature',
    kind: 'svg',
    width: 58,
    height: 32,
    render: Boulder,
  },
  {
    id: 'mushrooms',
    label: 'Mushrooms',
    category: 'nature',
    kind: 'svg',
    width: 40,
    height: 26,
    render: Mushrooms,
  },
  // Camp
  {
    id: 'tent',
    label: 'Tent',
    category: 'camp',
    kind: 'svg',
    width: 70,
    height: 58,
    render: Tent,
  },
  {
    id: 'campfire',
    label: 'Campfire',
    category: 'camp',
    kind: 'svg',
    width: 36,
    height: 46,
    render: Campfire,
  },
  {
    id: 'bench',
    label: 'Bench',
    category: 'camp',
    kind: 'svg',
    width: 56,
    height: 34,
    render: Bench,
  },
  {
    id: 'lamp-post',
    label: 'Lamp post',
    category: 'camp',
    kind: 'svg',
    width: 26,
    height: 70,
    render: LampPost,
  },
  {
    id: 'fence',
    label: 'Fence',
    category: 'camp',
    kind: 'svg',
    width: 74,
    height: 32,
    render: Fence,
  },
  // Landmarks
  {
    id: 'cabin',
    label: 'Cabin',
    category: 'landmarks',
    kind: 'svg',
    width: 74,
    height: 64,
    render: Cabin,
  },
  {
    id: 'signpost',
    label: 'Signpost',
    category: 'landmarks',
    kind: 'svg',
    width: 58,
    height: 48,
    render: c => <Signpost x={0} y={0} ground={c.ground} />,
  },
  {
    id: 'flag',
    label: 'Flag',
    category: 'landmarks',
    kind: 'svg',
    width: 32,
    height: 62,
    render: Flag,
  },
  {
    id: 'bridge',
    label: 'Bridge',
    category: 'landmarks',
    kind: 'svg',
    width: 90,
    height: 30,
    render: Bridge,
  },
  // Sky
  {
    id: 'cloud',
    label: 'Cloud',
    category: 'sky',
    kind: 'svg',
    width: 56,
    height: 36,
    render: Cloud,
  },
  {
    id: 'sun',
    label: 'Sun',
    category: 'sky',
    kind: 'svg',
    width: 60,
    height: 60,
    render: Sun,
  },
  {
    id: 'birds',
    label: 'Birds',
    category: 'sky',
    kind: 'svg',
    width: 44,
    height: 20,
    render: Birds,
  },
  {
    id: 'balloon',
    label: 'Balloon',
    category: 'sky',
    kind: 'svg',
    width: 46,
    height: 96,
    render: Balloon,
  },
  // Friends
  {
    id: 'beanie',
    label: 'Beanie',
    category: 'friends',
    kind: 'html',
    width: 70,
    height: 77,
    variants: PLUSH_VARIANTS,
    render: c => <PaiPlush size={70 * c.scale} state={plushState(c.variant)} />,
  },
  {
    id: 'ollie',
    label: 'Ollie',
    category: 'friends',
    kind: 'html',
    width: 70,
    height: 77,
    variants: PLUSH_VARIANTS,
    render: c => (
      <OlliePlush size={70 * c.scale} state={plushState(c.variant)} hasBeanie />
    ),
  },
  {
    id: 'plush-pi',
    label: 'Plush π',
    category: 'friends',
    kind: 'html',
    width: 70,
    height: 77,
    variants: PLUSH_VARIANTS,
    render: c => (
      <PlushPi size={70 * c.scale} state={plushState(c.variant)} hasBeanie />
    ),
  },
  // Probability toys
  {
    id: 'toy-die',
    label: 'Plush die',
    category: 'toys',
    kind: 'html',
    width: 60,
    height: 62,
    isInteractive: true,
    render: c => <ToyDie size={60 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'toy-coin',
    label: 'Coin',
    category: 'toys',
    kind: 'html',
    width: 56,
    height: 58,
    isInteractive: true,
    render: c => <ToyCoin size={56 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'toy-beans',
    label: 'Bean machine',
    category: 'toys',
    kind: 'html',
    width: 70,
    height: 86,
    isInteractive: true,
    render: c => <ToyBeanMachine size={70 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'toy-darts',
    label: 'π darts',
    category: 'toys',
    kind: 'html',
    width: 70,
    height: 72,
    isInteractive: true,
    render: c => <ToyDarts size={70 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'toy-marbles',
    label: 'Marble jar',
    category: 'toys',
    kind: 'html',
    width: 60,
    height: 76,
    isInteractive: true,
    render: c => <ToyMarbleJar size={60 * c.scale} isPreview={c.isPreview} />,
  },
  // AI toys
  {
    id: 'ai-autocomplete',
    label: 'Autocomplete',
    category: 'ai',
    kind: 'html',
    width: 84,
    height: 60,
    isInteractive: true,
    render: c => (
      <ToyAutocomplete size={84 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'ai-neuron',
    label: 'Neuron',
    category: 'ai',
    kind: 'html',
    width: 76,
    height: 70,
    isInteractive: true,
    render: c => <ToyNeuron size={76 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'ai-gradient',
    label: 'Gradient descent',
    category: 'ai',
    kind: 'html',
    width: 84,
    height: 68,
    isInteractive: true,
    render: c => (
      <ToyGradientDescent size={84 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'ai-temperature',
    label: 'Temperature',
    category: 'ai',
    kind: 'html',
    width: 80,
    height: 66,
    isInteractive: true,
    render: c => <ToyTemperature size={80 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'ai-classifier',
    label: 'Cat or dog?',
    category: 'ai',
    kind: 'html',
    width: 76,
    height: 70,
    isInteractive: true,
    render: c => <ToyClassifier size={76 * c.scale} isPreview={c.isPreview} />,
  },
  // Surprises
  {
    id: 'mystery-egg',
    label: 'Mystery egg',
    category: 'surprises',
    kind: 'html',
    width: 70,
    height: 94,
    isInteractive: true,
    render: c => <MysteryEgg size={70 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'rain-cloud',
    label: 'Rain cloud',
    category: 'surprises',
    kind: 'html',
    width: 70,
    height: 52,
    isInteractive: true,
    render: c => <ToyRainCloud size={70 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'cookie-oven',
    label: 'Cookie oven',
    category: 'surprises',
    kind: 'html',
    width: 120,
    height: 96,
    isInteractive: true,
    render: c => <ToyCookieOven size={120 * c.scale} isPreview={c.isPreview} />,
  },
  // Goodies
  {
    id: 'goodie-textbook',
    label: 'Course textbook',
    category: 'goodies',
    kind: 'html',
    width: 56,
    height: 62,
    link: {
      title: 'Course textbook',
      description: "Probability for AI, the course's free online book.",
      href: () => COURSE_TEXTBOOK_URL,
    },
    render: c => <GoodieTextbook size={56 * c.scale} />,
  },
  {
    id: 'goodie-playlist',
    label: 'Class playlist',
    category: 'goodies',
    kind: 'html',
    width: 84,
    height: 70,
    link: {
      title: 'Class playlist',
      description:
        'Suggest a song, rate the class picks, and see how probability chooses what plays.',
      href: ({courseId}) => `/${courseId}/music`,
    },
    render: c => <GoodieRecordPlayer size={84 * c.scale} />,
  },
  {
    id: 'goodie-tshirt',
    label: 'T-shirt contest',
    category: 'goodies',
    kind: 'html',
    width: 80,
    height: 80,
    link: {
      title: 'T-shirt contest',
      description:
        'Design the class T-shirt, vote for a favorite, and see the winner.',
      href: ({courseId}) => `/${courseId}/tshirt`,
    },
    render: c => <GoodieTshirt size={80 * c.scale} />,
  },
  {
    id: 'goodie-city-lights',
    label: 'City lights',
    category: 'goodies',
    kind: 'html',
    width: 70,
    height: 70,
    isInteractive: true,
    render: c => <ToyCityLights size={70 * c.scale} isPreview={c.isPreview} />,
  },
  // Stanford
  {
    id: 'stanford-hoover',
    label: 'Hoover Tower',
    category: 'stanford',
    kind: 'html',
    width: 50,
    height: 102,
    isInteractive: true,
    render: c => <ToyHooverTower size={50 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'stanford-tree',
    label: 'Dancing tree',
    category: 'stanford',
    kind: 'html',
    width: 64,
    height: 82,
    isInteractive: true,
    render: c => <ToyDancingTree size={64 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'stanford-bike',
    label: 'Bike',
    category: 'stanford',
    kind: 'html',
    width: 90,
    height: 58,
    isInteractive: true,
    render: c => <ToyBike size={90 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'stanford-fountain',
    label: 'Fountain hopping',
    category: 'stanford',
    kind: 'html',
    width: 84,
    height: 68,
    isInteractive: true,
    render: c => <ToyFountain size={84 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'stanford-dish',
    label: 'The Dish',
    category: 'stanford',
    kind: 'html',
    width: 84,
    height: 76,
    isInteractive: true,
    render: c => <ToyDish size={84 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'stanford-lake-lag',
    label: 'Lake Lagunita',
    category: 'stanford',
    kind: 'html',
    width: 96,
    height: 62,
    isInteractive: true,
    render: c => (
      <ToyLakeLagunita size={96 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'stanford-memchu',
    label: 'Memorial Church',
    category: 'stanford',
    kind: 'html',
    width: 84,
    height: 76,
    isInteractive: true,
    render: c => (
      <ToyMemorialChurch size={84 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'stanford-marguerite',
    label: 'The Marguerite',
    category: 'stanford',
    kind: 'html',
    width: 110,
    height: 59,
    isInteractive: true,
    render: c => <ToyMarguerite size={110 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'stanford-farm-cow',
    label: 'A cow from the Farm',
    category: 'stanford',
    kind: 'html',
    width: 80,
    height: 59,
    isInteractive: true,
    render: c => <ToyFarmCow size={80 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'stanford-jacaranda',
    label: 'Jacaranda',
    category: 'stanford',
    kind: 'html',
    width: 70,
    height: 79,
    isInteractive: true,
    render: c => <ToyJacaranda size={70 * c.scale} isPreview={c.isPreview} />,
  },
  // Ollie
  {
    id: 'ollie-float',
    label: 'Ollie floating',
    category: 'ollie',
    kind: 'html',
    width: 96,
    height: 62,
    isInteractive: true,
    render: c => <ToyOllieFloat size={96 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'ollie-holding-paws',
    label: 'Otters holding paws',
    category: 'ollie',
    kind: 'html',
    width: 104,
    height: 68,
    isInteractive: true,
    render: c => (
      <ToyOttersHoldingPaws size={104 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'ollie-juggling',
    label: 'Ollie juggling',
    category: 'ollie',
    kind: 'html',
    width: 64,
    height: 83,
    isInteractive: true,
    render: c => (
      <ToyOllieJuggling size={64 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'ollie-porch',
    label: "Ollie's porch",
    category: 'ollie',
    kind: 'html',
    width: 130,
    height: 102,
    isInteractive: true,
    render: c => <ToyOlliePorch size={130 * c.scale} isPreview={c.isPreview} />,
  },
  // Week 3
  {
    id: 'week3-mountain',
    label: 'Foggy mountain',
    category: 'week3',
    kind: 'html',
    width: 120,
    height: 101,
    isInteractive: true,
    render: c => (
      <ToyFoggyMountain size={120 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'week3-pyramid',
    label: 'Hidden chamber',
    category: 'week3',
    kind: 'html',
    width: 110,
    height: 92,
    isInteractive: true,
    render: c => <ToyPyramid size={110 * c.scale} isPreview={c.isPreview} />,
  },
  {
    id: 'week3-camera',
    label: 'Autofocus camera',
    category: 'week3',
    kind: 'html',
    width: 84,
    height: 71,
    isInteractive: true,
    render: c => (
      <ToyAutofocusCamera size={84 * c.scale} isPreview={c.isPreview} />
    ),
  },
  // Week 4
  {
    id: 'week4-slide',
    label: 'Sigmoid slide',
    category: 'week4',
    kind: 'html',
    width: 120,
    height: 85,
    isInteractive: true,
    render: c => (
      <ToySigmoidSlide size={120 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'week4-sorting-line',
    label: 'Sorting line',
    category: 'week4',
    kind: 'html',
    width: 150,
    height: 96,
    isInteractive: true,
    render: c => (
      <ToySortingLine size={150 * c.scale} isPreview={c.isPreview} />
    ),
  },
  {
    id: 'week4-doodle',
    label: 'Doodle machine',
    category: 'week4',
    kind: 'html',
    width: 110,
    height: 90,
    isInteractive: true,
    render: c => (
      <ToyDoodleMachine size={110 * c.scale} isPreview={c.isPreview} />
    ),
  },
  // Notes
  {
    id: 'note',
    label: 'Note',
    category: 'notes',
    kind: 'html',
    width: 90,
    height: 30,
    hasText: true,
    render: NoteLabel,
  },
];

/** Palette groupings with display names, in order. */
export const WIDGET_CATEGORIES: readonly {id: WidgetCategory; label: string}[] =
  [
    {id: 'week4', label: 'Week 4'},
    {id: 'week3', label: 'Week 3'},
    {id: 'toys', label: 'Probability toys'},
    {id: 'ai', label: 'AI toys'},
    {id: 'surprises', label: 'Surprises'},
    {id: 'goodies', label: 'Goodies'},
    {id: 'stanford', label: 'Stanford'},
    {id: 'ollie', label: 'Ollie'},
    {id: 'friends', label: 'Friends'},
    {id: 'nature', label: 'Nature'},
    {id: 'camp', label: 'Camp'},
    {id: 'landmarks', label: 'Landmarks'},
    {id: 'sky', label: 'Sky'},
    {id: 'notes', label: 'Notes'},
  ];

const WIDGETS_BY_ID = new Map(WIDGETS.map(widget => [widget.id, widget]));

/**
 * Looks a widget up by id.
 *
 * @param id - A widget id from a placement.
 * @return The definition, or undefined for an unknown id.
 */
export function widgetById(id: string): WidgetDefinition | undefined {
  return WIDGETS_BY_ID.get(id);
}
